import { Grid, Skeleton } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton } from '../../shared/components';
import { Urls, useApi, useAuth } from '../../shared/context';
import { useDataRoomDocumentsQuery, useDataRoomQuery, useOtherActiveDealsQuery } from '../../shared/hooks';
import { DataRoomDocument, DataRoomDownloadRequest, DataRoomFolder } from '../../shared/models';
import { AddFolderForm } from './AddFolderForm';
import { FolderBreadcrumbs } from './FolderBreadcrumbs';
import { FolderDisplay } from './FolderDisplay';

import { EditFolderForm } from './EditFolderForm';
import { RemoveFolderDialog } from './RemoveFolderDialog';
import { SendFolderLinkForm } from './SendFolderLinkForm';
import { EditDocumentForm } from './EditDocumentForm';
import { AddDocumentForm } from './AddDocumentForm';
import { RemoveDocumentDialog } from './RemoveDocumentDialog';
import { DownloadHistoryDetail } from './DownloadHistoryDetail';
import { SendDocumentForm } from './SendDocumentForm';
import { ShowDeletedDocuments } from './ShowDeletedDocuments';
import { LinkDocumentDrawer } from './LinkDocumentDrawer';
import { DownloadRequests } from './DownloadRequests';
import { useMutation } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useSnackbar } from 'notistack';
import { ShowAllDocuments } from './ShowAllDocuments';
import { DocumentPageActionButton } from './DocumentPageActionButton';
import { CopyDocumentsDrawer } from './CopyDocumentsDrawer';
import { CopyDocumentToDrawer } from './CopyDocumentToDrawer';
import { MoveDocumentForm } from './MoveDocumentForm';
import { MoveFolderDocumentsDrawer } from './MoveFolderDocumentsDrawer';
import { getApiResponseData } from '../../shared/utils/axios';

export const Documents: React.FC = () => {
	const api = useApi();
	const snackbar = useSnackbar();
	const { currentUser } = useAuth();
	const navigate = useNavigate();
	const { guid, folderGuid } = useParams();

	const dataRoomQuery = useDataRoomQuery(guid);
	const repositoryQuery = useDataRoomDocumentsQuery(guid, folderGuid, dataRoomQuery.data?.rootDataRoomFolderGuid, {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});
	const activeDealsQuery = useOtherActiveDealsQuery(guid);

	const [addFolder, setAddFolder] = React.useState<DataRoomFolder | null | undefined>(null);
	const [editFolder, setEditFolder] = React.useState<DataRoomFolder | null>(null);
	const [copyTo, setCopyTo] = React.useState<DataRoomDocument | null>(null);
	const [deleteFolder, setDeleteFolder] = React.useState<DataRoomFolder | null>(null);
	const [sendFolder, setSendFolder] = React.useState<DataRoomFolder | null>(null);

	const [addDocument, setAddDocument] = React.useState<DataRoomFolder | null>(null);
	const [editDocument, setEditDocument] = React.useState<DataRoomDocument | null>(null);
	const [moveDocument, setMoveDocument] = React.useState<DataRoomDocument | null>(null);
	const [downloadHistory, setDownloadHistory] = React.useState<DataRoomDocument | null>(null);
	const [deleteDocument, setDeleteDocument] = React.useState<DataRoomDocument | null>(null);
	const [sendDocument, setSendDocument] = React.useState<DataRoomDocument | null>(null);
	const [linkDocument, setLinkDocument] = React.useState<DataRoomDocument | null>(null);
	const [showDeleted, setShowDeleted] = React.useState<boolean>(false);
	const [showAll, setShowAll] = React.useState<boolean>(false);
	const [copyDocuments, setCopyDocuments] = React.useState<DataRoomFolder | null>(null);
	const [moveMultipleDocuments, setMoveMultipleDocuments] = React.useState<DataRoomFolder | null>(null);
	const canCopyDocuments =
		activeDealsQuery.data != null &&
		activeDealsQuery.data.length > 0 &&
		(repositoryQuery.data?.folder.name == 'Root' || repositoryQuery.data?.folder.name == 'Data Room Folder');

	const onChangeFolder = (folder: DataRoomFolder | null | undefined) => {
		onCloseDrawers();

		if (folder == null || folder == repositoryQuery.data?.folder) {
			return;
		}

		navigate(`/${guid}/${Urls.Documents.Root}/${folder.guid}`);

		// ugly hack, but only way it is currently working to repaint
		window.location.reload();
	};

	const onRequestDownload = () => {
		let currentFolder: string = '';
		if (folderGuid && folderGuid != '') {
			currentFolder = folderGuid;
		} else if (dataRoomQuery.isSuccess && dataRoomQuery.data.rootDataRoomFolderGuid) {
			currentFolder = dataRoomQuery.data.rootDataRoomFolderGuid;
		}

		if (currentFolder) {
			requestDownload.mutate(currentFolder);
		} else {
			snackbar.enqueueSnackbar('Could not find the folder information to download.', {
				variant: 'error',
			});
		}
	};

	const requestDownload = useMutation(
		async (folderGuid: string) => {
			const { data, error } = await getApiResponseData<DataRoomDownloadRequest>(
				api.post(CerebroApiEndpoints.Documents.FolderDownload(guid, folderGuid))
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar(
					'Your download request is being prepared, this can take a couple minutes to complete.',
					{ variant: 'success' }
				);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	const onCloseDrawers = () => {
		setAddFolder(null);
		setEditFolder(null);
		setDeleteFolder(null);
		setSendFolder(null);
		setCopyTo(null);
		setAddDocument(null);
		setEditDocument(null);
		setMoveDocument(null);
		setDownloadHistory(null);
		setDeleteDocument(null);
		setSendDocument(null);
		setShowDeleted(false);
		setShowAll(false);
		setLinkDocument(null);
		setCopyDocuments(null);
		setMoveMultipleDocuments(null);
	};

	return (
		<CerebroPageContainer maxWidth="95%">
			<CerebroBreadcrumb>Documents</CerebroBreadcrumb>

			{repositoryQuery.isLoading && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}

			{dataRoomQuery.isSuccess && repositoryQuery.isSuccess && activeDealsQuery.isSuccess && (
				<>
					<Grid container sx={{ marginBottom: 2 }}>
						{currentUser.isBankUser || currentUser.isTeamMemberUser ? (
							<>
								{repositoryQuery.data.totalDocuments > 0 && (
									<Grid item xs={12} md={4} sx={{ marginBottom: 3 }}>
										<CerebroSecondaryButton
											sx={{ marginRight: 1 }}
											onClick={() => onRequestDownload()}
										>
											{repositoryQuery.data.isRoot
												? 'Download All Files'
												: 'Download All Files in this Folder'}
										</CerebroSecondaryButton>
									</Grid>
								)}

								{repositoryQuery.data.totalDocuments > 0 && (
									<Grid item xs={12} md={4} sx={{ marginBottom: 3 }}>
										<CerebroSecondaryButton
											sx={{ marginRight: 1 }}
											onClick={() => setShowAll(true)}
										>
											Show All Documents
										</CerebroSecondaryButton>
									</Grid>
								)}
							</>
						) : (
							<Grid item xs={12} md={4} sx={{ marginBottom: 3 }}>
								<DocumentPageActionButton
									folder={repositoryQuery.data.folder}
									currentUser={currentUser}
									canCopyDocuments={canCopyDocuments}
									onDownload={() => onRequestDownload()}
									onAddFolder={() => setAddFolder(repositoryQuery.data.folder)}
									onAddFile={() => setAddDocument(repositoryQuery.data.folder)}
									onShowAll={() => setShowAll(true)}
									onShowDeleted={() => setShowDeleted(true)}
									onCopyDocuments={() => setCopyDocuments(repositoryQuery.data.folder)}
									onMoveMultipleDocuments={() =>
										setMoveMultipleDocuments(repositoryQuery.data.folder)
									}
								/>
							</Grid>
						)}

						<Grid item xs={12} md={8}>
							<DownloadRequests
								dataRoomGuid={guid}
								currentUser={currentUser}
								isReady={repositoryQuery.isSuccess}
							/>
						</Grid>
					</Grid>

					<Grid container sx={{ marginBottom: 2 }}>
						<Grid item xs={12}>
							<FolderBreadcrumbs
								dataRoom={dataRoomQuery.data}
								repository={repositoryQuery.data}
								changeFolder={onChangeFolder}
							/>
						</Grid>
					</Grid>

					<FolderDisplay
						dataRoomGuid={guid}
						repository={repositoryQuery.data}
						currentUser={currentUser}
						activeDeals={activeDealsQuery.data}
						changeFolder={onChangeFolder}
						editFolder={setEditFolder}
						deleteFolder={setDeleteFolder}
						sendFolder={setSendFolder}
						editDocument={setEditDocument}
						moveDocument={setMoveDocument}
						copyTo={setCopyTo}
						downloadHistory={setDownloadHistory}
						deleteDocument={setDeleteDocument}
						sendDocument={setSendDocument}
						linkDocument={setLinkDocument}
						copyDocuments={setCopyDocuments}
					/>

					<AddFolderForm dataRoomGuid={guid} folder={addFolder} close={onChangeFolder} />
					<EditFolderForm dataRoomGuid={guid} folder={editFolder} close={onChangeFolder} />
					<RemoveFolderDialog dataRoomGuid={guid} folder={deleteFolder} close={onCloseDrawers} />
					<SendFolderLinkForm dataRoomGuid={guid} folder={sendFolder} close={onCloseDrawers} />

					<AddDocumentForm dataRoomGuid={guid} folder={addDocument} close={onCloseDrawers} />
					<EditDocumentForm
						dataRoomGuid={guid}
						document={editDocument}
						folder={repositoryQuery.data.folder}
						close={onCloseDrawers}
					/>
					<MoveDocumentForm dataRoomGuid={guid} document={moveDocument} close={onCloseDrawers} />
					<DownloadHistoryDetail dataRoomGuid={guid} document={downloadHistory} close={onCloseDrawers} />
					<SendDocumentForm dataRoomGuid={guid} document={sendDocument} close={onCloseDrawers} />
					<RemoveDocumentDialog
						dataRoomGuid={guid}
						folder={repositoryQuery.data.folder}
						document={deleteDocument}
						close={onCloseDrawers}
					/>
					<ShowDeletedDocuments dataRoomGuid={guid} showDocuments={showDeleted} close={onCloseDrawers} />
					<ShowAllDocuments dataRoomGuid={guid} showDocuments={showAll} close={onCloseDrawers} />
					<LinkDocumentDrawer dataRoomGuid={guid} document={linkDocument} close={onCloseDrawers} />
					<CopyDocumentsDrawer
						dataRoomGuid={guid}
						activeDeals={activeDealsQuery.data}
						folder={copyDocuments}
						close={onCloseDrawers}
					/>
					<MoveFolderDocumentsDrawer
						dataRoomGuid={guid}
						folder={moveMultipleDocuments}
						close={onCloseDrawers}
					/>
					<CopyDocumentToDrawer dataRoomGuid={guid} document={copyTo} close={onCloseDrawers} />
				</>
			)}
		</CerebroPageContainer>
	);
};
